import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { 
  ChatConversation, 
  ChatMessageNew, 
  ChatMessageReaction, 
  ChatConversationParticipant,
  ChatRoleSettings,
  UserChatPermissions 
} from '../types';

interface ChatContextType {
  // Estado
  conversations: ChatConversation[];
  currentConversation: ChatConversation | null;
  messages: ChatMessageNew[];
  participants: ChatConversationParticipant[];
  roleSettings: ChatRoleSettings[];
  userPermissions: UserChatPermissions | null;
  isLoading: boolean;
  error: string | null;

  // Acciones
  setCurrentConversation: (conversation: ChatConversation | null) => void;
  createConversation: (name: string, description: string, isPrivate: boolean) => Promise<void>;
  joinConversation: (conversationId: string) => Promise<void>;
  leaveConversation: (conversationId: string) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  editMessage: (messageId: string, newMessage: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  reactToMessage: (messageId: string, reactionType: 'like' | 'dislike') => Promise<void>;
  removeReaction: (messageId: string) => Promise<void>;
  updateRoleSettings: (roleSettings: ChatRoleSettings[]) => Promise<void>;
  
  // Utilidades
  canAccessChat: boolean;
  canCreateConversations: boolean;
  canJoinConversations: boolean;
  canSendMessages: boolean;
  canEditMessages: boolean;
  canDeleteMessages: boolean;
  canReactToMessages: boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessageNew[]>([]);
  const [participants, setParticipants] = useState<ChatConversationParticipant[]>([]);
  const [roleSettings, setRoleSettings] = useState<ChatRoleSettings[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserChatPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Obtener permisos del usuario
  const fetchUserPermissions = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('get_user_chat_permissions', {
        user_uuid: user.id
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setUserPermissions(data[0]);
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  }, [user]);

  // Obtener conversaciones
  const fetchConversations = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .select(`
          *,
          participant_count:chat_conversation_participants(count)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Procesar el participant_count para convertir {count: X} a X
      const processedData = data?.map(conversation => ({
        ...conversation,
        participant_count: typeof conversation.participant_count === 'object' && conversation.participant_count?.count 
          ? conversation.participant_count.count 
          : 0
      }));

      setConversations(processedData || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Error al cargar las conversaciones');
    }
  }, [user]);

  // Obtener mensajes de una conversación
  const fetchMessages = useCallback(async (conversationId: string) => {
    if (!user || !conversationId) return;

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Procesar mensajes para incluir reacciones y información de usuario
      const messagesWithReactions = await Promise.all(
        (data || []).map(async (message) => {
          try {
            // Obtener información del usuario
            const { data: userData } = await supabase
              .rpc('get_users_with_roles')
              .eq('id', message.user_id)
              .single();

            // Obtener reacciones
            const { data: reactions } = await supabase
              .from('chat_message_reactions')
              .select('*')
              .eq('message_id', message.id);

            const likes = reactions?.filter(r => r.reaction_type === 'like').length || 0;
            const dislikes = reactions?.filter(r => r.reaction_type === 'dislike').length || 0;
            const userReaction = reactions?.find(r => r.user_id === user.id)?.reaction_type || null;

            return {
              ...message,
              user: userData ? {
                email: userData.email,
                role: userData.role
              } : null,
              reactions: {
                likes,
                dislikes,
                user_reaction: userReaction
              }
            };
          } catch (error) {
            console.warn('Error processing message:', error);
            return {
              ...message,
              user: null,
              reactions: {
                likes: 0,
                dislikes: 0,
                user_reaction: null
              }
            };
          }
        })
      );

      setMessages(messagesWithReactions);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Error al cargar los mensajes');
    }
  }, [user]);

  // Obtener participantes de una conversación
  const fetchParticipants = useCallback(async (conversationId: string) => {
    if (!user || !conversationId) return;

    try {
      const { data, error } = await supabase
        .from('chat_conversation_participants')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('joined_at', { ascending: true });

      if (error) throw error;

      setParticipants(data || []);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  }, [user]);

  // Obtener configuraciones de roles
  const fetchRoleSettings = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('chat_role_settings')
        .select('*')
        .order('role_name');

      if (error) throw error;

      setRoleSettings(data || []);
    } catch (error) {
      console.error('Error fetching role settings:', error);
    }
  }, [user]);

  // Crear conversación
  const createConversation = async (name: string, description: string, isPrivate: boolean) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .insert({
          name,
          description,
          created_by: user.id,
          is_private: isPrivate
        })
        .select()
        .single();

      if (error) throw error;

      // Unirse automáticamente a la conversación creada
      await joinConversation(data.id);
      
      // Actualizar lista de conversaciones
      await fetchConversations();
      
      // Establecer como conversación actual
      setCurrentConversation(data);
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  };

  // Unirse a conversación
  const joinConversation = async (conversationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('chat_conversation_participants')
        .insert({
          conversation_id: conversationId,
          user_id: user.id,
          role: 'participant'
        });

      if (error) throw error;

      // Actualizar participantes
      await fetchParticipants(conversationId);
    } catch (error) {
      console.error('Error joining conversation:', error);
      throw error;
    }
  };

  // Salir de conversación
  const leaveConversation = async (conversationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('chat_conversation_participants')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Si es la conversación actual, limpiar
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
        setParticipants([]);
      }

      // Actualizar participantes
      await fetchParticipants(conversationId);
    } catch (error) {
      console.error('Error leaving conversation:', error);
      throw error;
    }
  };

  // Enviar mensaje
  const sendMessage = async (message: string) => {
    if (!user || !currentConversation) return;

    // Crear mensaje optimista para mostrar instantáneamente
    const optimisticMessage: ChatMessageNew = {
      id: `temp-${Date.now()}`,
      conversation_id: currentConversation.id,
      user_id: user.id,
      message,
      message_type: 'text',
      created_at: new Date().toISOString(),
      is_edited: false,
      edited_at: null,
      reactions: []
    };

    // Agregar mensaje optimista inmediatamente
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: currentConversation.id,
          user_id: user.id,
          message,
          message_type: 'text'
        })
        .select()
        .single();

      if (error) throw error;

      // Reemplazar mensaje optimista con el real
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticMessage.id ? { ...data, reactions: [] } : msg
      ));

    } catch (error) {
      console.error('Error sending message:', error);
      // Remover mensaje optimista en caso de error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      throw error;
    }
  };

  // Editar mensaje
  const editMessage = async (messageId: string, newMessage: string) => {
    if (!user) return;

    // Actualización optimista
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, message: newMessage, is_edited: true, edited_at: new Date().toISOString() }
        : msg
    ));

    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({
          message: newMessage,
          is_edited: true,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error editing message:', error);
      // Revertir cambio optimista en caso de error
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, message: msg.message, is_edited: false, edited_at: null }
          : msg
      ));
      throw error;
    }
  };

  // Eliminar mensaje
  const deleteMessage = async (messageId: string) => {
    if (!user) return;

    // Eliminación optimista
    setMessages(prev => prev.filter(msg => msg.id !== messageId));

    try {
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('id', messageId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting message:', error);
      // Revertir eliminación optimista en caso de error
      await fetchMessages(currentConversation!.id);
      throw error;
    }
  };

  // Reaccionar a mensaje
  const reactToMessage = async (messageId: string, reactionType: 'like' | 'dislike') => {
    if (!user) return;

    // Actualización optimista de reacciones
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        const currentReactions = msg.reactions || [];
        const existingReactionIndex = currentReactions.findIndex(r => r.user_id === user.id);
        
        let newReactions = [...currentReactions];
        
        if (existingReactionIndex >= 0) {
          // Cambiar reacción existente
          newReactions[existingReactionIndex] = {
            ...newReactions[existingReactionIndex],
            reaction_type: reactionType
          };
        } else {
          // Agregar nueva reacción
          newReactions.push({
            id: `temp-${Date.now()}`,
            message_id: messageId,
            user_id: user.id,
            reaction_type: reactionType,
            created_at: new Date().toISOString()
          });
        }
        
        return { ...msg, reactions: newReactions };
      }
      return msg;
    }));

    try {
      // Usar upsert para evitar duplicados y manejar cambios de reacción
      const { error } = await supabase
        .from('chat_message_reactions')
        .upsert({
          message_id: messageId,
          user_id: user.id,
          reaction_type: reactionType
        }, {
          onConflict: 'message_id,user_id,reaction_type'
        });

      if (error) throw error;

      // No necesitamos fetchMessages aquí, las reacciones ya están actualizadas
    } catch (error) {
      console.error('Error reacting to message:', error);
      // Revertir cambio optimista en caso de error
      await fetchMessages(currentConversation!.id);
      throw error;
    }
  };

  // Remover reacción
  const removeReaction = async (messageId: string) => {
    if (!user) return;

    // Eliminación optimista de reacción
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        const currentReactions = msg.reactions || [];
        const newReactions = currentReactions.filter(r => r.user_id !== user.id);
        return { ...msg, reactions: newReactions };
      }
      return msg;
    }));

    try {
      const { error } = await supabase
        .from('chat_message_reactions')
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', user.id);

      if (error) throw error;

      // No necesitamos fetchMessages aquí, las reacciones ya están actualizadas
    } catch (error) {
      console.error('Error removing reaction:', error);
      // Revertir cambio optimista en caso de error
      await fetchMessages(currentConversation!.id);
    }
  };

  // Actualizar configuraciones de roles
  const updateRoleSettings = async (roleSettings: ChatRoleSettings[]) => {
    if (!user) return;

    try {
      // Actualizar cada configuración de rol
      for (const roleSetting of roleSettings) {
        const { error } = await supabase
          .from('chat_role_settings')
          .update({
            can_access_chat_page: roleSetting.can_access_chat_page,
            can_create_conversations: roleSetting.can_create_conversations,
            can_join_conversations: roleSetting.can_join_conversations,
            can_send_messages: roleSetting.can_send_messages,
            can_edit_messages: roleSetting.can_edit_messages,
            can_delete_messages: roleSetting.can_delete_messages,
            can_react_to_messages: roleSetting.can_react_to_messages
          })
          .eq('role_name', roleSetting.role_name);

        if (error) throw error;
      }

      // Actualizar lista de configuraciones
      await fetchRoleSettings();
    } catch (error) {
      console.error('Error updating role settings:', error);
      throw error;
    }
  };

  // Configurar realtime para mensajes
  useEffect(() => {
    if (!user || !currentConversation) return;

    const channel = supabase
      .channel(`chat:${currentConversation.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${currentConversation.id}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new as ChatMessageNew;
            
            // Solo procesar si no es un mensaje optimista (temp-)
            if (!newMessage.id.startsWith('temp-')) {
              // Obtener información del usuario usando la función existente
              if (newMessage.user_id) {
                try {
                  const { data: userData } = await supabase
                    .rpc('get_users_with_roles')
                    .eq('id', newMessage.user_id)
                    .single();

                  if (userData) {
                    newMessage.user = {
                      email: userData.email,
                      role: userData.role
                    };
                  }
                } catch (error) {
                  console.warn('Error fetching user info for realtime message:', error);
                }
              }

              // Inicializar reacciones vacías para evitar fetch adicional
              newMessage.reactions = {
                likes: 0,
                dislikes: 0,
                user_reaction: null
              };

              setMessages(prev => [...prev, newMessage]);
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedMessage = payload.new as ChatMessageNew;
            setMessages(prev => 
              prev.map(msg => 
                msg.id === updatedMessage.id ? { ...msg, ...updatedMessage } : msg
              )
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedMessage = payload.old as ChatMessageNew;
            setMessages(prev => prev.filter(msg => msg.id !== deletedMessage.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, currentConversation]);

  // Configurar realtime para participantes
  useEffect(() => {
    if (!user || !currentConversation) return;

    const channel = supabase
      .channel(`participants:${currentConversation.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_conversation_participants',
          filter: `conversation_id=eq.${currentConversation.id}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const newParticipant = payload.new as ChatConversationParticipant;
            
            // Obtener información del usuario
            const { data: userData } = await supabase
              .from('auth.users')
              .select('email, raw_app_meta_data->>\'app_role\' as role')
              .eq('id', newParticipant.user_id)
              .single();

            if (userData) {
              newParticipant.user = {
                email: userData.email,
                role: userData.role
              };
            }

            setParticipants(prev => [...prev, newParticipant]);
          } else if (payload.eventType === 'DELETE') {
            const deletedParticipant = payload.old as ChatConversationParticipant;
            setParticipants(prev => prev.filter(p => p.id !== deletedParticipant.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, currentConversation]);

  // Cargar datos iniciales
  useEffect(() => {
    if (!user) return;

    const loadInitialData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await Promise.all([
          fetchUserPermissions(),
          fetchConversations(),
          fetchRoleSettings()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Error al cargar los datos del chat');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [user, fetchUserPermissions, fetchConversations, fetchRoleSettings]);

  // Cargar mensajes y participantes cuando cambie la conversación actual
  useEffect(() => {
    if (currentConversation) {
      fetchMessages(currentConversation.id);
      fetchParticipants(currentConversation.id);
    } else {
      setMessages([]);
      setParticipants([]);
    }
  }, [currentConversation, fetchMessages, fetchParticipants]);

  // Computar permisos del usuario
  const canAccessChat = userPermissions?.can_access_chat_page || false;
  const canCreateConversations = userPermissions?.can_create_conversations || false;
  const canJoinConversations = userPermissions?.can_join_conversations || false;
  const canSendMessages = userPermissions?.can_send_messages || false;
  const canEditMessages = userPermissions?.can_edit_messages || false;
  const canDeleteMessages = userPermissions?.can_delete_messages || false;
  const canReactToMessages = userPermissions?.can_react_to_messages || false;

  const value: ChatContextType = {
    // Estado
    conversations,
    currentConversation,
    messages,
    participants,
    roleSettings,
    userPermissions,
    isLoading,
    error,

    // Acciones
    setCurrentConversation,
    createConversation,
    joinConversation,
    leaveConversation,
    sendMessage,
    editMessage,
    deleteMessage,
    reactToMessage,
    removeReaction,
    updateRoleSettings,

    // Utilidades
    canAccessChat,
    canCreateConversations,
    canJoinConversations,
    canSendMessages,
    canEditMessages,
    canDeleteMessages,
    canReactToMessages,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
